import React from "react";
import { formatDateTime, formatNumber, formatUsd } from "@/lib/format";
import { formatAdminRunStatus } from "./adminFormat";

const text = (value) => value === null || value === undefined || value === "" ? "—" : String(value);
const number = (value) => value === null || value === undefined ? "—" : formatNumber(value);
const money = (value) => value === null || value === undefined ? "—" : formatUsd(value);
const date = (value) => value ? formatDateTime(value) : "—";
const status = (value) => value === "error" ? "Erreur" : formatAdminRunStatus(value);

export function duration(value) {
  if (value === null || value === undefined) return "—";
  if (value < 1000) return `${number(value)} ms`;
  if (value < 60000) return `${number(Math.round(value / 100) / 10)} s`;
  const minutes = Math.floor(value / 60000);
  const seconds = Math.round((value % 60000) / 1000);
  return seconds ? `${number(minutes)} min ${number(seconds)} s` : `${number(minutes)} min`;
}

function DataTable({ title, rows, columns, empty }) {
  return (
    <section className="og-card min-w-0 overflow-hidden">
      <h3 className="border-b border-border px-5 py-4 font-heading text-base font-semibold">{title}</h3>
      {!rows.length ? <p className="p-5 text-sm text-muted-foreground">{empty}</p> : (
        <div role="region" aria-label={title} tabIndex={0} className="max-w-full overflow-x-auto og-focus">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground"><tr>
              {columns.map(({ label }) => <th key={label} scope="col" className="whitespace-nowrap px-4 py-3 font-medium">{label}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((row, index) => <tr key={row.id || index} className="hover:bg-muted/30">
                {columns.map(({ label, value }, cellIndex) => cellIndex === 0
                  ? <th key={label} scope="row" className="break-words px-4 py-3 font-medium">{value(row)}</th>
                  : <td key={label} className="break-words px-4 py-3">{value(row)}</td>)}
              </tr>)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function UsageSections({ data }) {
  const byOrg = Array.isArray(data?.by_organization) ? data.by_organization : [];
  const byModel = Array.isArray(data?.by_model) ? data.by_model : [];
  const byAgent = Array.isArray(data?.by_agent) ? data.by_agent : [];
  const totals = byOrg.reduce((sum, row) => ({
    cost: sum.cost + Number(row.cost_usd ?? 0),
    calls: sum.calls + Number(row.calls ?? 0),
    tokens: sum.tokens + Number(row.tokens ?? 0),
  }), { cost: 0, calls: 0, tokens: 0 });
  return (
    <div className="min-w-0 space-y-5">
      <h3 className="font-heading text-xl font-semibold">Usage IA — 30 derniers jours</h3>
      {byOrg.length > 0 && <div className="grid gap-3 sm:grid-cols-3">
        {[["Coût IA", money(totals.cost)], ["Appels IA", number(totals.calls)], ["Tokens", number(totals.tokens)]].map(([label, value]) => (
          <div key={label} className="og-card min-w-0 p-5"><p className="text-sm text-muted-foreground">{label}</p><p className="mt-2 break-words font-heading text-2xl font-semibold">{value}</p></div>
        ))}
      </div>}
      <DataTable title="Par organisation" rows={byOrg} empty="Aucun usage IA par organisation." columns={[
        { label: "Organisation", value: (r) => text(r.organization) },
        { label: "Identifiant", value: (r) => text(r.organization_id) },
        { label: "Coût USD", value: (r) => money(r.cost_usd) },
        { label: "Tokens", value: (r) => number(r.tokens) },
        { label: "Appels", value: (r) => number(r.calls) },
      ]} />
      <DataTable title="Par modèle" rows={byModel} empty="Aucun usage IA par modèle." columns={[
        { label: "Fournisseur", value: (r) => text(r.provider) },
        { label: "Modèle", value: (r) => text(r.model) },
        { label: "Coût USD", value: (r) => money(r.cost_usd) },
        { label: "Tokens", value: (r) => number(r.tokens) },
        { label: "Appels", value: (r) => number(r.calls) },
        { label: "Latence moyenne", value: (r) => duration(r.avg_latency_ms) },
      ]} />
      <DataTable title="Par agent" rows={byAgent} empty="Aucun usage IA par agent." columns={[
        { label: "Agent", value: (r) => text(r.agent_id) },
        { label: "Coût USD", value: (r) => money(r.cost_usd) },
        { label: "Appels", value: (r) => number(r.calls) },
      ]} />
    </div>
  );
}

export function RunsSection({ data }) {
  return <DataTable title="Exécutions" rows={Array.isArray(data) ? data : []} empty="Aucune exécution." columns={[
    { label: "Organisation", value: (r) => text(r.organization_name) },
    { label: "Agent", value: (r) => text(r.agent_id) },
    { label: "Tâche", value: (r) => text(r.task_type) },
    { label: "Statut", value: (r) => status(r.status) },
    { label: "Déclencheur", value: (r) => text(r.trigger) },
    { label: "Date", value: (r) => date(r.created_at) },
    { label: "Durée", value: (r) => duration(r.duration_ms) },
    { label: "Tokens", value: (r) => number(r.total_tokens) },
    { label: "Coût estimé USD", value: (r) => money(r.estimated_cost_usd) },
  ]} />;
}

export function ErrorsSections({ data }) {
  const agentRuns = Array.isArray(data?.agent_runs) ? data.agent_runs : [];
  const aiCalls = Array.isArray(data?.ai_calls) ? data.ai_calls : [];
  const automationRuns = Array.isArray(data?.automation_runs) ? data.automation_runs : [];
  return <div className="min-w-0 space-y-5">
    <DataTable title="Exécutions d’agents en erreur" rows={agentRuns} empty="Aucune exécution d’agent en erreur." columns={[
      { label: "Organisation", value: (r) => text(r.organization_name) },
      { label: "Agent", value: (r) => text(r.agent_id) },
      { label: "Tâche", value: (r) => text(r.task_type) },
      { label: "Statut", value: (r) => status(r.status) },
      { label: "Date", value: (r) => date(r.created_at) },
      { label: "Durée", value: (r) => duration(r.duration_ms) },
    ]} />
    <DataTable title="Appels IA en erreur" rows={aiCalls} empty="Aucun appel IA en erreur." columns={[
      { label: "Organisation", value: (r) => text(r.organization_name) },
      { label: "Agent", value: (r) => text(r.agent_id) },
      { label: "Tâche", value: (r) => text(r.task_type) },
      { label: "Fournisseur", value: (r) => text(r.provider) },
      { label: "Modèle", value: (r) => text(r.model) },
      { label: "Statut", value: (r) => status(r.status) },
      { label: "Date", value: (r) => date(r.created_at) },
      { label: "Latence", value: (r) => duration(r.latency_ms) },
      { label: "Coût estimé USD", value: (r) => money(r.estimated_cost_usd) },
    ]} />
    <DataTable title="Automatisations en erreur" rows={automationRuns} empty="Aucune automatisation en erreur." columns={[
      { label: "Organisation", value: (r) => text(r.organization_name) },
      { label: "Statut", value: (r) => status(r.status) },
      { label: "Date", value: (r) => date(r.created_at) },
      { label: "Terminée le", value: (r) => date(r.finished_at) },
    ]} />
  </div>;
}

export function ActivitySection({ data }) {
  return <DataTable title="Activité" rows={Array.isArray(data) ? data : []} empty="Aucune activité récente." columns={[
    { label: "Date", value: (r) => date(r.created_at) },
    { label: "Organisation", value: (r) => text(r.organization_name) },
    { label: "Type d’acteur", value: (r) => text(r.actor_type) },
    { label: "Action", value: (r) => text(r.action) },
    { label: "Type d’entité", value: (r) => text(r.entity_type) },
  ]} />;
}